import { useEffect, useState } from "react";
import { useForm, useController } from "react-hook-form";
import { X, Loader2, Settings, Terminal, Copy, CheckCircle, Code, Eye, EyeOff } from "lucide-react";
import Input from "../../widgets/Input";
import {
  MCPConfig,
  CreateMCPConfigData,
  UpdateMCPConfigData,
} from "../../services/mcpConfig.service";
import Tooltip from "../../widgets/ToolTip";

interface MCPConfigFormProps {
  isOpen: boolean;
  onClose: () => void;
  config: MCPConfig | null;
  onSubmit: (
    data: CreateMCPConfigData | UpdateMCPConfigData
  ) => Promise<void>;
  isLoading: boolean;
}

export default function MCPConfigForm({
  isOpen,
  onClose,
  config,
  onSubmit,
  isLoading,
}: MCPConfigFormProps) {
  const { control, handleSubmit, reset } = useForm<CreateMCPConfigData>();
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  const isEdit = !!config;

  useEffect(() => {
    if (isOpen) {
      if (config) {
        reset({
          mcp_name: config.mcp_name,
          mcp_connection_npx_command: config.mcp_connection_npx_command,
        });
      } else {
        reset({
          mcp_name: "",
          mcp_connection_npx_command: "",
        });
      }
    }
  }, [isOpen, config, reset]);

  const handleFormSubmit = (data: CreateMCPConfigData) => {
    onSubmit(data);
  };

  // NPX Command Field Component
  const NPXCommandField = ({ name, control, label, rules }: any) => {
    const {
      field: { onChange, onBlur, value = "", ref },
      fieldState: { error },
    } = useController({
      name,
      control,
      rules,
      defaultValue: "",
    });

    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const formatJSON = () => {
      try {
        if (value && value.trim().startsWith('{')) {
          const parsed = JSON.parse(value);
          const formatted = JSON.stringify(parsed, null, 2);
          onChange(formatted);
        }
      } catch (err) {
        // Invalid JSON, don't format
      }
    };

    const isValidJSON = () => {
      try {
        if (value && value.trim().startsWith('{')) {
          JSON.parse(value);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    const exampleCommands = [
      {
        name: "Brave Search",
        command: `{"mcpServers":{"brave-search":{"command":"npx","args":["-y","@modelcontextprotocol/server-brave-search"],"env":{"BRAVE_API_KEY":"YOUR_API_KEY_HERE"}}}}`,
      },
      {
        name: "File System",
        command: `{"mcpServers":{"filesystem":{"command":"npx","args":["-y","@modelcontextprotocol/server-filesystem","/path/to/allowed/dir"]}}}`,
      },
      {
        name: "Simple URL",
        command: "https://example.com/mcp-server",
      },
    ];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {rules?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center gap-2">
            {value && (
              <button
                type="button"
                onClick={() => copyToClipboard(value)}
                className={`p-1 rounded transition-colors text-xs flex items-center gap-1 ${
                  copied
                    ? "text-green-600 hover:text-green-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title={copied ? "Copied!" : "Copy command"}
              >
                {copied ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            )}
            {value && value.trim().startsWith('{') && (
              <button
                type="button"
                onClick={formatJSON}
                className="p-1 rounded transition-colors text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700"
                title="Format JSON"
              >
                <Code className="w-3 h-3" />
                Format
              </button>
            )}
            <Tooltip content={showPreview ? "Hide examples" : "Show examples"}>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="p-1 rounded transition-colors text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700"
            >
              {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showPreview ? "Hide" : "Examples"}
            </button>
            </Tooltip>
          </div>
        </div>

        <div className="relative">
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            rows={6}
            className={`w-full px-3 py-3 border rounded-lg shadow-sm resize-none font-mono text-sm transition-colors duration-200 ${
              error
                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                : isValidJSON()
                ? "border-green-300 focus:ring-green-500 focus:border-green-500"
                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            } focus:outline-none focus:ring-2`}
            placeholder="Enter NPX command (JSON format) or URL..."
            disabled={isLoading}
          />
          {value && value.trim().startsWith('{') && (
            <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${
              isValidJSON() ? "bg-green-400" : "bg-red-400"
            }`} title={isValidJSON() ? "Valid JSON" : "Invalid JSON"} />
          )}
        </div>

        {error && (
          <div className="text-red-500 text-sm flex items-center gap-1">
            <X className="w-3 h-3" />
            {error.message}
          </div>
        )}

        {showPreview && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Terminal className="w-4 h-4" />
              Example Commands
            </h4>
            <div className="space-y-3">
              {exampleCommands.map((example, index) => (
                <div key={index} className="group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">
                      {example.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => onChange(example.command)}
                      className="opacity-0 group-hover:opacity-100 text-xs text-blue-600 hover:text-blue-700 transition-opacity"
                    >
                      Use this
                    </button>
                  </div>
                  <div className="bg-white border rounded p-2 font-mono text-xs text-gray-600 break-all">
                    {example.command.length > 100
                      ? `${example.command.substring(0, 100)}...`
                      : example.command
                    }
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
              <h5 className="text-xs font-medium text-blue-900 mb-1">Format Guide:</h5>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• <strong>JSON Format:</strong> Full MCP server configuration with commands and environment variables</li>
                <li>• <strong>URL Format:</strong> Simple URL pointing to an MCP server endpoint</li>
                <li>• Use the "Format" button to beautify JSON or "Examples" to see templates</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit
              ? "Edit MCP Configuration"
              : "Create New MCP Configuration"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isLoading}
          >
            <Tooltip content="Close">
            <X className="w-5 h-5" />
            </Tooltip>
          </button>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="p-6 space-y-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Basic Information
              </h3>
            </div>

            <Input
              name="mcp_name"
              control={control}
              label="MCP Name"
              placeholder="e.g., Brave Search MCP"
              rules={{ required: "MCP name is required" }}
            />
          </div>

          {/* Connection Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Terminal className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Connection Command
              </h3>
            </div>

            <NPXCommandField
              name="mcp_connection_npx_command"
              control={control}
              label="NPX Connection Command"
              rules={{ required: "Connection command is required" }}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isEdit ? "Updating..." : "Creating..."}</span>
                </>
              ) : (
                <span>
                  {isEdit ? "Update Configuration" : "Create Configuration"}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}